import React, { useEffect, useState } from "react";
import { Grid, GridColumn, GridFilterChangeEvent, GridSortChangeEvent, GridRowClickEvent, GridCellProps, GridDataStateChangeEvent } from "@progress/kendo-react-grid";
import { process, CompositeFilterDescriptor, SortDescriptor } from "@progress/kendo-data-query";

import { useInjection } from "@presentation/components/dependencyInjection";
import { ArchiveRepository } from "@infrastructure/repositories/ArchiveRepository";
import { Job } from "@domain/schemas/BI";
import { useJobSetupStore } from "@presentation/stores/JobSetupStore";
import { Toolbar, Button } from "@progress/kendo-react-buttons";
import { Breadcrumb, BreadcrumbLinkMouseEvent } from "@progress/kendo-react-layout";
import { SvgIcon } from "@progress/kendo-react-common";
import * as svgIcons from "@progress/kendo-svg-icons";

import { Checkbox } from "@progress/kendo-react-inputs";

const DATA_ITEM_KEY = "id";
const SELECTED_FIELD = "selected";
const ROOT_ARCHIVE_BREADCRUMB = { id: "home", text: "Archives" };

let persistedArchiveBreadcrumbItems: { id: string; text: string }[] = [ROOT_ARCHIVE_BREADCRUMB];
let persistedArchiveCurrentPath = "home";

type ShiftSelectionEvent = React.SyntheticEvent<Element> & {
    shiftKey?: boolean;
    nativeEvent: Event & { shiftKey?: boolean };
};

export interface ArchiveGridItem {
    id: string;
    name: string;
    path: string;
    isFolder: boolean;
    sizeInBytes?: number;
    createdAt?: string;
    jobName?: string;
    jobAnalysisName?: string;
    jobCreatedAt?: string;
}

type ArchiveRepositoryItem = {
    path: string;
    sizeInBytes?: number;
    createdAt?: string;
};

const LoadingMask: React.FC = () => (
    <div className="k-loading-mask">
        <span className="k-loading-text">Loading...</span>
        <div className="k-loading-image"></div>
        <div className="k-loading-color"></div>
    </div>
);

const SizeCell: React.FC<GridCellProps> = (props) => (
    <td
        style={props.style}
        className={props.className}
        colSpan={props.colSpan}
        role={'gridcell'}
        aria-colindex={props.ariaColumnIndex}
        aria-selected={props.isSelected}
    >
        {props.dataItem.sizeInGiB != null ? props.dataItem.sizeInGiB.toFixed(2) + " GB" : "—"}
    </td>
);

const DateCell: React.FC<GridCellProps> = (props) => {
    const { dataItem } = props;
    if (!dataItem.createdAt) return (
        <td
            style={props.style}
            className={props.className}
            colSpan={props.colSpan}
            role={'gridcell'}
            aria-colindex={props.ariaColumnIndex}
            aria-selected={props.isSelected}
        >
            —
        </td>
    );
    const d = new Date(dataItem.createdAt);
    const formatted = d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" })
        + " " + d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
    return (
        <td
            style={props.style}
            className={props.className}
            colSpan={props.colSpan}
            role={'gridcell'}
            aria-colindex={props.ariaColumnIndex}
            aria-selected={props.isSelected}
            title={d.toISOString()}
        >
            {formatted}
        </td>
    );
};

const FolderIcon: React.FC = () => (
    <SvgIcon icon={svgIcons.folderIcon} style={{ marginRight: "4px", color: "#f5a623", flexShrink: 0 }} />
);

const FileIcon: React.FC = () => (
    <SvgIcon icon={svgIcons.fileDataIcon} style={{ marginRight: "4px", color: "#666", flexShrink: 0 }} />
);

export interface ArchiveExplorerGridProps {
    onLoadingChange?: (isLoading: boolean) => void;
    preloadedItems?: ArchiveGridItem[];
}

export const ArchiveExplorerGrid: React.FC<ArchiveExplorerGridProps> = ({ onLoadingChange, preloadedItems }) => {
    const archiveRepository = useInjection(ArchiveRepository);
    const [items, setItems] = useState<ArchiveGridItem[]>(preloadedItems || []);
    const [loading, setLoading] = useState<boolean>(!preloadedItems);
    const [filter, setFilter] = useState<CompositeFilterDescriptor>();
    const [sort, setSort] = useState<SortDescriptor[]>([{ field: "createdAt", dir: "desc" }]);
    const [skip, setSkip] = useState<number>(0);
    const [take, setTake] = useState<number>(20);

    // Store completed jobs with their metadata for fetching archives
    const [completedJobsMap, setCompletedJobsMap] = useState<Map<string, Job>>(new Map());

    // Initialize map if preloaded items are provided
    useEffect(() => {
        if (preloadedItems && preloadedItems.length > 0) {
            const jobsMap = new Map<string, Job>();
            preloadedItems.forEach(item => {
                if (item.isFolder) {
                    jobsMap.set(item.id, {
                        id: item.id,
                        name: item.name,
                        createdAt: item.jobCreatedAt || item.createdAt,
                        analysisName: item.jobAnalysisName
                    } as Job);
                }
            });
            setCompletedJobsMap(jobsMap);
        }
    }, [preloadedItems]);

    const [breadcrumbItems, setBreadcrumbItemsState] = useState<{ id: string; text: string }[]>(persistedArchiveBreadcrumbItems);
    const [currentPath, setCurrentPathState] = useState<string>(persistedArchiveCurrentPath);

    const [selection, setSelection] = useState<Record<string, boolean>>({});
    const [selectedFolders, setSelectedFolders] = useState<Record<string, boolean>>({});
    const selectionAnchorIdRef = React.useRef<string | null>(null);

    const { setJobArchiveFiles, archiveFiles } = useJobSetupStore((state) => ({
        setJobArchiveFiles: state.setJobArchiveFiles,
        archiveFiles: state.CreateJobState.archiveFiles || []
    }));

    const setBreadcrumbItems = (newBreadcrumbItems: { id: string; text: string }[]) => {
        persistedArchiveBreadcrumbItems = newBreadcrumbItems;
        setBreadcrumbItemsState(newBreadcrumbItems);
    };

    const setCurrentPath = (newCurrentPath: string) => {
        persistedArchiveCurrentPath = newCurrentPath;
        setCurrentPathState(newCurrentPath);
    };

    // Sync store state to local selection
    useEffect(() => {
        const newSelection: Record<string, boolean> = {};
        
        // Add files from store
        archiveFiles.forEach(f => {
            newSelection[f.path] = true;
        });

        // Add locally selected folders, merging with existing selection
        // We ensure we don't accidentally select a file with same ID as a folder (unlikely with path vs UUID)
        Object.keys(selectedFolders).forEach(folderId => {
            if (selectedFolders[folderId]) {
                newSelection[folderId] = true;
            }
        });

        setSelection(newSelection);
    }, [archiveFiles, selectedFolders]);

    // Sync items with current selection state
    useEffect(() => {
        // Items are already updated in the data processing
    }, [items, selection]);

    // Fetch items based on current path
    const fetchItems = async (path: string) => {
        // If root, we rely on preloadedItems via useEffect, so do nothing here
        if (path === "" || path === "home") {
            return;
        }

        setLoading(true);
        onLoadingChange?.(true);
        console.log("Fetching items for path:", path);
        try {
            // Fetch archives from selected job's PulsarStep3 folder using the API
            const job = completedJobsMap.get(path);
            
            // Fallback: If job not in map (maybe preloadedItems didn't populate map fully or reference issue),
            // try to find it in items if items are currently the job list.
            let targetJob = job;
            if (!targetJob) {
                const item = items.find(i => i.id === path);
                if (item && item.isFolder) {
                    targetJob = {
                            id: item.id,
                            name: item.name,
                            createdAt: item.jobCreatedAt || item.createdAt,
                            analysisName: item.jobAnalysisName
                        } as Job;
                        // Update map for future
                        setCompletedJobsMap(prev => new Map(prev).set(path, targetJob!));
                }
            }

            if (!targetJob) {
                console.error("Job not found in map:", path);
                setItems([]);
                return;
            }

            console.log("Fetching archives for job:", targetJob.name, "createdAt:", targetJob.createdAt);
            
            const archives = await archiveRepository.getJobArchives(
                targetJob.createdAt ?? new Date().toISOString(),
                targetJob.name ?? targetJob.id,
                targetJob.analysisName ?? undefined
            );
            
            console.log("Archives response:", archives);
            
            const archiveItems: ArchiveGridItem[] = (archives as ArchiveRepositoryItem[]).map((archive) => ({
                id: archive.path,
                name: archive.path.split('/').pop() || archive.path,
                path: archive.path,
                isFolder: false,
                sizeInBytes: archive.sizeInBytes,
                createdAt: archive.createdAt
            }));
            
            console.log("Archive items:", archiveItems);
            setItems(archiveItems);
        } catch (error) {
            console.error("Error fetching items:", error);
            setItems([]);
        } finally {
            setLoading(false);
            onLoadingChange?.(false);
        }
    };

    useEffect(() => {
        if (currentPath === "home" || currentPath === "") {
             if (preloadedItems) {
                 setItems(preloadedItems);
                 setLoading(false);
             }
        } else {
             fetchItems(currentPath);
        }
    }, [currentPath, preloadedItems]);

    useEffect(() => {
        selectionAnchorIdRef.current = null;
    }, [currentPath]);

    const handleBreadcrumbClick = async (event: BreadcrumbLinkMouseEvent) => {
        const clickedItemIndex = breadcrumbItems.findIndex((item) => item.id === event.id);
        const newBreadcrumbs = breadcrumbItems.slice(0, clickedItemIndex + 1);
        const path = event.id === "home" ? "" : (event.id ?? "");
        setCurrentPath(path);
        setBreadcrumbItems(newBreadcrumbs);
        setSkip(0);
    };

    const navigateToFolder = (item: ArchiveGridItem) => {
        if (item.isFolder) {
            const newBreadcrumbs = [...breadcrumbItems, { id: item.id, text: item.name }];
            setBreadcrumbItems(newBreadcrumbs);
            setCurrentPath(item.id);
            setSkip(0);
        }
    };

    const handleRowClick = async (event: GridRowClickEvent) => {
        navigateToFolder(event.dataItem);
    };

    const hasShiftKey = (event: ShiftSelectionEvent) => {
        if (typeof event.shiftKey === "boolean") {
            return event.shiftKey;
        }

        return Boolean(event.nativeEvent?.shiftKey);
    };

    const applySelectionForItem = (
        item: ArchiveGridItem,
        isChecked: boolean,
        nextArchiveFiles: { path: string; sizeInBytes: number }[],
        nextSelectedFolders: Record<string, boolean>,
    ) => {
        if (item.isFolder) {
            if (isChecked) {
                nextSelectedFolders[item.id] = true;
            } else {
                delete nextSelectedFolders[item.id];
            }
        }

        const existingIndex = nextArchiveFiles.findIndex((file) => file.path === item.path);
        if (isChecked) {
            if (existingIndex === -1) {
                nextArchiveFiles.push({ path: item.path, sizeInBytes: item.isFolder ? 0 : (item.sizeInBytes || 0) });
            }
        } else if (existingIndex !== -1) {
            nextArchiveFiles.splice(existingIndex, 1);
        }
    };

    const toggleSelection = (item: ArchiveGridItem, isChecked: boolean, event?: ShiftSelectionEvent) => {
        const anchorId = selectionAnchorIdRef.current;
        const isShiftRangeSelection = Boolean(event && hasShiftKey(event) && anchorId);
        const nextArchiveFiles = [...archiveFiles];
        const nextSelectedFolders = { ...selectedFolders };

        if (isShiftRangeSelection) {
            const anchorIndex = visibleItems.findIndex((visibleItem) => visibleItem.id === anchorId);
            const selectedIndex = visibleItems.findIndex((visibleItem) => visibleItem.id === item.id);

            if (anchorIndex >= 0 && selectedIndex >= 0) {
                const [startIndex, endIndex] = anchorIndex < selectedIndex ? [anchorIndex, selectedIndex] : [selectedIndex, anchorIndex];
                visibleItems.slice(startIndex, endIndex + 1).forEach((visibleItem) => {
                    applySelectionForItem(visibleItem, isChecked, nextArchiveFiles, nextSelectedFolders);
                });

                setSelectedFolders(nextSelectedFolders);
                setJobArchiveFiles(nextArchiveFiles);
                selectionAnchorIdRef.current = item.id;
                return;
            }
        }

        applySelectionForItem(item, isChecked, nextArchiveFiles, nextSelectedFolders);
        setSelectedFolders(nextSelectedFolders);
        setJobArchiveFiles(nextArchiveFiles);
        selectionAnchorIdRef.current = item.id;
    };

    const handleFilterChange = (event: GridFilterChangeEvent) => {
        setFilter(event.filter);
        setSkip(0);
    };

    const handleSortChange = (event: GridSortChangeEvent) => {
        setSort(event.sort);
    };

    const handleDataStateChange = (event: GridDataStateChangeEvent) => {
        setFilter(event.dataState.filter);
        setSort(event.dataState.sort ?? []);
        setSkip(event.dataState.skip ?? 0);
        setTake(event.dataState.take ?? 20);
    };

    const data = items.map(item => ({
        ...item,
        [SELECTED_FIELD]: !!selection[item.id],
        sizeInGiB: item.sizeInBytes == null ? null : item.sizeInBytes / (1024 * 1024 * 1024)
    }));

    const processedData = process(data, { filter, sort, skip, take });
    const visibleItems = processedData.data as ArchiveGridItem[];

    const navigateBack = () => {
        if (breadcrumbItems.length > 1) {
            const newBreadcrumbs = breadcrumbItems.slice(0, -1);
            const path = newBreadcrumbs.length === 1 ? "" : newBreadcrumbs[newBreadcrumbs.length - 1].id;
            setBreadcrumbItems(newBreadcrumbs);
            setCurrentPath(path);
            setSkip(0);
        }
    };

    return (
        <div className="fill-all" style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
            <Toolbar className="page-toolbar" style={{ flex: "0 0 auto", height: "auto", padding: "8px", borderBottom: "1px solid #e0e0e0", display: "flex", alignItems: "center", gap: "10px" }}>
                <Breadcrumb
                    data={breadcrumbItems}
                    onItemSelect={handleBreadcrumbClick}
                    style={{ flex: 1 }}
                />
                {breadcrumbItems.length > 1 && (
                    <Button onClick={navigateBack} icon="arrow-left" title="Go back">
                    </Button>
                )}
            </Toolbar>

            <div style={{ flex: "1 1 auto", minHeight: 0, width: "100%" }}>
                <Grid
                    key={currentPath}
                    style={{ height: "100%", width: "100%" }}
                    data={processedData.data}
                    total={processedData.total}
                    dataItemKey={DATA_ITEM_KEY}
                    selectedField={SELECTED_FIELD}
                    onRowClick={handleRowClick}
                    sortable={true}
                    sort={sort}
                    onSortChange={handleSortChange}
                    filterable={true}
                    filter={filter}
                    onFilterChange={handleFilterChange}
                    scrollable="scrollable"
                    resizable={true}
                    pageable={{ pageSizes: [10, 20, 50, 100], buttonCount: 5 }}
                    skip={skip}
                    take={take}
                    onDataStateChange={handleDataStateChange}
                >
                    {loading && <LoadingMask />}
                    <GridColumn 
                        field={SELECTED_FIELD} 
                        title="Select" 
                        width="50" 
                        filterable={false} 
                        sortable={false}
                        cell={(props) => (
                            <td
                                style={props.style}
                                className={props.className}
                                colSpan={props.colSpan}
                                role={'gridcell'}
                                aria-colindex={props.ariaColumnIndex}
                                aria-selected={props.isSelected}
                            >
                                <Checkbox 
                                    checked={props.dataItem[SELECTED_FIELD]}
                                    onChange={(e) => {
                                        if (e.syntheticEvent) {
                                            e.syntheticEvent.stopPropagation();
                                        }
                                        toggleSelection(props.dataItem, Boolean(e.value), e.syntheticEvent as ShiftSelectionEvent);
                                    }}
                                />
                            </td>
                        )}
                    />
                    <GridColumn 
                        field="name" 
                        title="Name" 
                        width="350" 
                        cell={(props) => (
                            <td
                                style={props.style}
                                className={props.className}
                                colSpan={props.colSpan}
                                role={'gridcell'}
                                aria-colindex={props.ariaColumnIndex}
                                aria-selected={props.isSelected}
                                onClick={() => navigateToFolder(props.dataItem)}
                            >
                                <div style={{ display: "flex", alignItems: "center", cursor: props.dataItem.isFolder ? "pointer" : "default", width: "100%", overflow: "hidden" }} title={props.dataItem.name}>
                                    {props.dataItem.isFolder ? <FolderIcon /> : <FileIcon />}
                                    <span style={{ textOverflow: "ellipsis", whiteSpace: "nowrap", overflow: "hidden" }}>
                                        {props.dataItem.name}
                                    </span>
                                </div>
                            </td>
                        )} 
                    />
                    <GridColumn field="path" title="Path" width="auto" />
                    <GridColumn
                        field="createdAt"
                        title="Created"
                        width="150"
                        cell={DateCell}
                    />
                    <GridColumn
                        field="sizeInGiB"
                        title="Size"
                        width="170"
                        cell={SizeCell}
                        filter="numeric"
                    />
                </Grid>
            </div>
        </div>
    );
};
